import { useEffect, useState, useCallback, Fragment } from "react";
import { useLocation } from "react-router-dom";
import { api, apiErr, pdfUrl } from "../lib/api";
import { money, fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useClientPerms } from "../lib/useClientPerms";
import { toast } from "sonner";
import { UploadCloud, Download, FileSpreadsheet, RotateCcw, CheckCircle2, ArrowRight } from "lucide-react";

const YEARS = [2024, 2025, 2026];
const SOURCES = ["QuickBooks Payroll", "Wagepoint", "ADP", "Ceridian", "Payworks", "Manual", "Other"];
const norm = (s) => String(s || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

export default function Migration() {
  const { user } = useAuth();
  const location = useLocation();
  const isClient = user?.role !== "super_admin";
  const canEdit = useClientPerms().canEdit("migration");

  const [companyId, setCompanyId] = useState("");
  const [year, setYear] = useState(2026);
  const [status, setStatus] = useState(null);
  const [batches, setBatches] = useState([]);
  const [nhc, setNhc] = useState({});
  const [company, setCompany] = useState(null);
  const isMaster = user?.role === "super_admin" && (user?.staff_role || "master") === "master";
  const migClosed = !!company?.migration_closed;
  const toggleClosed = async () => {
    const closing = !migClosed;
    if (!window.confirm(closing
      ? "Close migration for this client? The Migration tab, readiness banners and first-run nudges will be hidden for everyone. Opening balances stay on file and you can reopen any time."
      : "Reopen migration for this client? The Migration tab and readiness checks will show again.")) return;
    try {
      await api.post(`/companies/${companyId}/migration/close`, { closed: closing });
      toast.success(closing ? "Migration closed" : "Migration reopened");
      api.get(`/companies/${companyId}`).then((r) => setCompany(r.data));
      window.dispatchEvent(new Event("company-settings-changed"));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const [expanded, setExpanded] = useState({});

  // importer
  const [fields, setFields] = useState([]);
  const [source, setSource] = useState("QuickBooks Payroll");
  const [parsed, setParsed] = useState(null); // {columns, rows, filename}
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null); // {rows, summary}
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1); // 1 upload, 2 map, 3 preview

  useEffect(() => {
    api.get("/companies").then((r) => {
      const qs = new URLSearchParams(location.search).get("company");
      setCompanyId(qs || r.data[0]?.company_id || "");
    });
  }, [location.search]);

  const loadStatus = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}`).then((r) => setCompany(r.data)).catch(() => setCompany(null));
    api.get(`/companies/${companyId}/migration/status?tax_year=${year}`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
    api.get(`/companies/${companyId}/migration/batches`).then((r) => setBatches(r.data || [])).catch(() => setBatches([]));
    api.get(`/companies/${companyId}/migration/fields`).then((r) => setFields(r.data?.fields || [])).catch(() => setFields([]));
    api.get(`/companies/${companyId}/new-hire-confirmations?tax_year=${year}`).then((r) => {
      const m = {}; (r.data || []).forEach((n) => { m[n.employee_id] = n; }); setNhc(m);
    }).catch(() => setNhc({}));
  }, [companyId, year]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const confirmNH = async (eid) => {
    try { await api.post(`/companies/${companyId}/employees/${eid}/confirm-new-hire`, { tax_year: year, note: "" }); toast.success("New hire confirmed"); loadStatus(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const unconfirmNH = async (eid) => {
    try { await api.delete(`/companies/${companyId}/employees/${eid}/confirm-new-hire?tax_year=${year}`); toast.success("Removed"); loadStatus(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const dlReport = (fmt) => window.open(pdfUrl(`/companies/${companyId}/migration/audit-report.${fmt}?tax_year=${year}`), "_blank");

  const resetImporter = () => { setParsed(null); setMapping({}); setPreview(null); setStep(1); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const cols = r.data?.columns || [];
      setParsed({ ...r.data, filename: file.name });
      // auto-map by normalized label match
      const m = {};
      fields.forEach((f) => {
        const hit = cols.find((c) => norm(c) === norm(f.label) || norm(c) === norm(f.key));
        if (hit) m[f.key] = hit;
      });
      setMapping(m);
      setStep(2);
      toast.success(`Parsed ${r.data?.count || 0} row(s)`);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/companies/${companyId}/migration/preview`, {
        tax_year: year, source_system: source, mapping, rows: parsed.rows,
      });
      setPreview(r.data); setStep(3);
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/companies/${companyId}/migration/commit`, {
        tax_year: year, source_system: source, source_filename: parsed.filename, mapping, rows: parsed.rows,
      });
      const b = r.data?.batch;
      toast.success(`Imported ${b.accepted_rows + b.warning_rows} balance(s) · ${b.rejected_rows} rejected`);
      resetImporter(); loadStatus();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const rollback = async (bid) => {
    if (!window.confirm("Roll back this import? This is only allowed if no imported balance has affected finalized payroll.")) return;
    try {
      const r = await api.post(`/companies/${companyId}/migration/batches/${bid}/rollback`);
      toast.success(`Rolled back ${r.data?.reverted || 0} balance(s)`);
      loadStatus();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const dl = (fmt) => window.open(pdfUrl(`/companies/${companyId}/migration/template.${fmt}`), "_blank");

  const STAT = status?.counts || {};
  const STAT_CARDS = [
    ["Total employees", STAT.total, "text-black"],
    ["Migrated", STAT.migrated, "text-emerald-600"],
    ["Needs review", STAT.needs_review, "text-amber-600"],
    ["Not started", STAT.not_started, "text-muted-foreground"],
  ];
  const badge = (s) => ({ accepted: "bg-emerald-100 text-emerald-700", warning: "bg-amber-100 text-amber-700", rejected: "bg-red-100 text-red-700" }[s] || "bg-neutral-100");

  return (
    <div className="w-full" data-testid="migration-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-black tracking-tight">Migration</h1>
          <p className="text-sm text-muted-foreground">Opening YTD balances from a prior payroll system — used only while onboarding a new client.</p>
        </div>
        {isMaster && companyId && (
          <label className="inline-flex items-center gap-2 border border-black px-3 py-2 text-xs font-mono uppercase tracking-widest cursor-pointer select-none" data-testid="migration-close-toggle-wrap">
            <input type="checkbox" data-testid="migration-close-toggle" className="h-4 w-4 accent-black" checked={migClosed} onChange={toggleClosed} />
            {migClosed ? "Migration closed" : "Close migration"}
          </label>
        )}
        <select data-testid="migration-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black">
          {YEARS.map((y) => <option key={y} value={y}>Tax year {y}</option>)}
        </select>
        <button data-testid="migration-audit-pdf" onClick={() => dlReport("pdf")} className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-sm hover:border-black transition-colors"><Download className="h-3.5 w-3.5" /> Audit report (PDF)</button>
        <button data-testid="migration-audit-csv" onClick={() => dlReport("csv")} className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-sm hover:border-black transition-colors"><Download className="h-3.5 w-3.5" /> CSV</button>
      </div>

      {migClosed && (
        <div className="border-l-4 border-emerald-600 bg-emerald-50 p-4 mb-6 text-sm" data-testid="migration-closed-notice">
          <div className="font-black tracking-tight text-emerald-800">Migration closed{company?.migration_closed_at ? ` · ${String(company.migration_closed_at).slice(0, 10)}` : ""}{company?.migration_closed_by ? ` · ${company.migration_closed_by}` : ""}</div>
          <p className="text-emerald-900/80 text-xs mt-1">Onboarding for this client is complete. The Migration tab, readiness banners and first-run nudges are hidden. Opening balances below are kept for the audit trail{isMaster ? " — untick “Migration closed” above to reopen." : "."}</p>
        </div>
      )}

      {/* Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="migration-dashboard">
        {STAT_CARDS.map(([label, val, cls]) => (
          <div key={label} className="border border-black p-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`text-3xl font-black tabular-nums ${cls}`}>{val ?? "—"}</div>
          </div>
        ))}
      </div>
      {status && !status.ruleset_available && (
        <div className="text-xs border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-amber-800 mb-4" data-testid="migration-no-ruleset">
          No {year} ruleset on file — statutory maximum/room checks are unavailable for this year.
        </div>
      )}

      {/* Importer */}
      {canEdit && !migClosed && (
        <div className="border border-black mb-6" data-testid="migration-importer">
          <div className="bg-neutral-50 px-4 py-2.5 flex items-center justify-between border-b border-border">
            <span className="text-sm font-black tracking-tight">Import opening balances</span>
            <div className="flex items-center gap-2">
              <button data-testid="migration-template-xlsx" onClick={() => dl("xlsx")} className="inline-flex items-center gap-1.5 text-xs border border-border px-2.5 py-1.5 hover:border-black transition-colors"><Download className="h-3.5 w-3.5" /> XLSX template</button>
              <button data-testid="migration-template-csv" onClick={() => dl("csv")} className="inline-flex items-center gap-1.5 text-xs border border-border px-2.5 py-1.5 hover:border-black transition-colors"><Download className="h-3.5 w-3.5" /> CSV template</button>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {/* stepper */}
            <div className="flex items-center gap-2 text-xs">
              {[["1", "Upload"], ["2", "Map & validate"], ["3", "Preview & confirm"]].map(([n, l], i) => (
                <div key={n} className="flex items-center gap-2">
                  <span className={`h-6 w-6 flex items-center justify-center font-bold ${step >= Number(n) ? "bg-black text-white" : "bg-neutral-200 text-muted-foreground"}`}>{n}</span>
                  <span className={step >= Number(n) ? "font-medium" : "text-muted-foreground"}>{l}</span>
                  {i < 2 && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
                </div>
              ))}
            </div>

            {step === 1 && (
              <div className="flex flex-wrap items-center gap-3">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Source system</label>
                  <select data-testid="migration-source" value={source} onChange={(e) => setSource(e.target.value)} className="border border-input bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black">
                    {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <label data-testid="migration-upload" className="cursor-pointer inline-flex items-center gap-2 bg-black text-white px-4 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity mt-4">
                  <UploadCloud className="h-4 w-4" /> {busy ? "Reading…" : "Upload CSV or XLSX"}
                  <input type="file" accept=".csv,.xlsx" className="hidden" onChange={onFile} disabled={busy} />
                </label>
              </div>
            )}

            {step === 2 && parsed && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm"><FileSpreadsheet className="h-4 w-4" /> {parsed.filename} · {parsed.count} rows</div>
                <p className="text-xs text-muted-foreground">Map spreadsheet columns to fields. At least one match field (Employee ID / number / email / name) is required per row.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 max-h-[340px] overflow-y-auto border border-border p-3">
                  {fields.map((f) => (
                    <div key={f.key} className="flex items-center gap-2 py-1">
                      <span className="text-xs w-1/2 truncate" title={f.label}><span className="text-[9px] font-mono uppercase text-muted-foreground mr-1">{f.group}</span>{f.label}</span>
                      <select data-testid={`migration-map-${f.key}`} value={mapping[f.key] || ""} onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))} className="flex-1 border border-input bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-black">
                        <option value="">—</option>
                        {parsed.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button data-testid="migration-validate" onClick={runPreview} disabled={busy} className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"><CheckCircle2 className="h-4 w-4" /> {busy ? "Validating…" : "Validate & preview"}</button>
                  <button data-testid="migration-cancel" onClick={resetImporter} className="border border-border px-4 py-2 text-sm hover:border-black transition-colors">Cancel</button>
                </div>
              </div>
            )}

            {step === 3 && preview && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 text-xs" data-testid="migration-preview-summary">
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700">Accepted {preview.summary.accepted}</span>
                  <span className="px-2 py-1 bg-amber-100 text-amber-700">Warnings {preview.summary.warning}</span>
                  <span className="px-2 py-1 bg-red-100 text-red-700">Rejected {preview.summary.rejected}</span>
                </div>
                <div className="border border-border max-h-[360px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-neutral-50 sticky top-0"><tr className="text-left">
                      <th className="px-2 py-1.5">Row</th><th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5">Employee</th><th className="px-2 py-1.5">Gross</th><th className="px-2 py-1.5">CPP</th><th className="px-2 py-1.5">EI</th><th className="px-2 py-1.5">Notes</th>
                    </tr></thead>
                    <tbody>
                      {preview.rows.map((r) => (
                        <Fragment key={r.row}>
                        <tr className="border-t border-border/60" data-testid={`migration-row-${r.row}`}>
                          <td className="px-2 py-1.5">{r.row}</td>
                          <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 ${badge(r.status)}`}>{r.status}</span>{r.is_correction && <span className="ml-1 px-1.5 py-0.5 bg-sky-100 text-sky-700">correction</span>}</td>
                          <td className="px-2 py-1.5">{r.matched_name || <span className="text-red-600">{r.match_status}</span>}</td>
                          <td className="px-2 py-1.5 tabular-nums">{money(r.amounts.gross_employment_income || 0)}</td>
                          <td className="px-2 py-1.5 tabular-nums">{money(r.amounts.cpp_employee || 0)}</td>
                          <td className="px-2 py-1.5 tabular-nums">{money(r.amounts.ei_employee || 0)}</td>
                          <td className="px-2 py-1.5 text-[11px] text-muted-foreground">
                            {(r.diff || []).length > 0 && <button data-testid={`migration-diff-toggle-${r.row}`} onClick={() => setExpanded((x) => ({ ...x, [r.row]: !x[r.row] }))} className="mr-2 underline">{expanded[r.row] ? "hide" : "diff"} ({r.diff.length})</button>}
                            {[...r.errors, ...r.warnings].join("; ")}
                          </td>
                        </tr>
                        {expanded[r.row] && (r.diff || []).length > 0 && (
                          <tr key={`${r.row}-diff`} className="bg-neutral-50" data-testid={`migration-diff-${r.row}`}>
                            <td colSpan={7} className="px-4 py-2">
                              <table className="w-full text-[11px]">
                                <thead><tr className="text-left text-muted-foreground"><th className="py-1">Field</th><th>Existing</th><th>Imported</th><th>Difference</th></tr></thead>
                                <tbody>
                                  {r.diff.map((d) => (
                                    <tr key={d.field} className="border-t border-border/40">
                                      <td className="py-1">{d.field}{d.t4_box ? ` (Box ${d.t4_box})` : ""}</td>
                                      <td className="tabular-nums">{money(d.existing)}</td>
                                      <td className="tabular-nums font-medium">{money(d.imported)}</td>
                                      <td className={`tabular-nums ${d.difference >= 0 ? "text-emerald-700" : "text-red-700"}`}>{d.difference >= 0 ? "+" : ""}{money(d.difference)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2">
                  <button data-testid="migration-confirm" onClick={commit} disabled={busy || (preview.summary.accepted + preview.summary.warning) === 0} className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">{busy ? "Importing…" : `Confirm import (${preview.summary.accepted + preview.summary.warning})`}</button>
                  <button data-testid="migration-back" onClick={() => setStep(2)} className="border border-border px-4 py-2 text-sm hover:border-black transition-colors">Back</button>
                  <button onClick={resetImporter} className="border border-border px-4 py-2 text-sm hover:border-black transition-colors">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reconciliation table */}
      <div className="border border-black mb-6">
        <div className="bg-neutral-50 px-4 py-2.5 border-b border-border text-sm font-black tracking-tight">Migration reconciliation — opening vs PostWage vs combined</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="migration-reconciliation-table">
            <thead className="bg-neutral-50 text-xs"><tr className="text-left">
              <th className="px-3 py-2">Employee</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Effective</th>
              <th className="px-3 py-2 text-right">Opening gross</th><th className="px-3 py-2 text-right">Combined gross</th>
              <th className="px-3 py-2 text-right">CPP room</th><th className="px-3 py-2 text-right">CPP2 room</th><th className="px-3 py-2 text-right">EI room</th><th className="px-3 py-2 text-right">Vac opening</th><th className="px-3 py-2">New hire</th>
            </tr></thead>
            <tbody>
              {(status?.rows || []).map((r) => (
                <tr key={r.employee_id} className="border-t border-border/60" data-testid={`migration-emp-${r.employee_id}`}>
                  <td className="px-3 py-2 font-medium">{r.name}{r.needs_review && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700">review</span>}</td>
                  <td className="px-3 py-2 text-xs">{r.has_opening_balance ? r.source_system : <span className="text-muted-foreground">not started</span>}</td>
                  <td className="px-3 py-2 text-xs">{r.effective_as_of_date || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.opening.gross)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.combined.gross)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.remaining_room.cpp == null ? "—" : money(r.remaining_room.cpp)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.remaining_room.cpp2 == null ? "—" : money(r.remaining_room.cpp2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.remaining_room.ei == null ? "—" : money(r.remaining_room.ei)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(r.opening.vacation_balance)}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.has_opening_balance ? <span className="text-muted-foreground">n/a</span>
                      : nhc[r.employee_id] ? (
                        <span className="inline-flex items-center gap-2"><span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700">confirmed</span>{canEdit && !isClient && <button data-testid={`migration-nh-remove-${r.employee_id}`} onClick={() => unconfirmNH(r.employee_id)} className="underline text-muted-foreground">remove</button>}</span>
                      ) : canEdit ? (
                        <button data-testid={`migration-nh-confirm-${r.employee_id}`} onClick={() => confirmNH(r.employee_id)} className="border border-border px-2 py-1 hover:border-black transition-colors">Confirm new hire</button>
                      ) : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
              {(status?.rows || []).length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">No employees.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Import history */}
      <div className="border border-black" data-testid="migration-history">
        <div className="bg-neutral-50 px-4 py-2.5 border-b border-border text-sm font-black tracking-tight">Import history</div>
        <div className="divide-y divide-border/60">
          {batches.map((b) => (
            <div key={b.batch_id} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid={`migration-batch-${b.batch_id}`}>
              <span className="font-mono">{b.batch_id}</span>
              <span>{b.source_system}</span>
              <span className="text-muted-foreground">{b.source_filename || "manual"}</span>
              <span className="text-muted-foreground">{fmtDateTime(b.imported_at)} · {b.imported_by}</span>
              <span className="inline-flex gap-2">
                <span className="text-emerald-600">{b.accepted_rows + b.warning_rows} imported</span>
                {b.rejected_rows > 0 && <span className="text-red-600">{b.rejected_rows} rejected</span>}
              </span>
              {b.status === "rolled_back" ? (
                <span className="ml-auto px-2 py-0.5 bg-neutral-100 text-muted-foreground">rolled back</span>
              ) : canEdit ? (
                <button data-testid={`migration-rollback-${b.batch_id}`} onClick={() => rollback(b.batch_id)} className="ml-auto inline-flex items-center gap-1.5 border border-border px-2.5 py-1 hover:border-red-600 hover:text-red-600 transition-colors"><RotateCcw className="h-3.5 w-3.5" /> Roll back</button>
              ) : null}
            </div>
          ))}
          {batches.length === 0 && <div className="px-4 py-8 text-center text-muted-foreground text-sm">No imports yet.</div>}
        </div>
      </div>
    </div>
  );
}
